// Skills.js
import React from "react";
import { useTheme } from "../contexts/ThemeContext";
import { motion } from "framer-motion";

import html from "../assets/logos/html5.svg";
import css from "../assets/logos/css3.svg";
import javascript from "../assets/logos/javascript.svg";
import react from "../assets/logos/react.svg";
import Nodejs from "../assets/logos/nodedotjs.svg";
import postgresql from "../assets/logos/postgresql.svg";
import git from "../assets/logos/git.svg";
import github from "../assets/logos/github.svg";
import netlify from "../assets/logos/netlify.svg";
import vercel from "../assets/logos/vercel.svg";
import bootstrap from "../assets/logos/bootstrap.svg";
import tailwindcss from "../assets/logos/tailwindcss.svg";
import cplusplus from "../assets/logos/cplusplus.svg";
import c from "../assets/logos/c.svg";
import python from "../assets/logos/python.svg";
import django from "../assets/logos/django.svg";
import typescript from "../assets/logos/typescript.svg";
import nextjs from "../assets/logos/nextjs.svg";
import fastapi from "../assets/logos/fastapi.svg";

const skillLogos = {
  HTML: html,
  CSS: css,
  TailwindCSS: tailwindcss,
  JavaScript: javascript,
  TypeScript: typescript,
  React: react,
  Nextjs: nextjs,
  FastAPI: fastapi,
  Nodejs: Nodejs,
  PostgreSQL: postgresql,
  Git: git,
  GitHub: github,
  Netlify: netlify,
  Vercel: vercel,
  Bootstrap: bootstrap,
  "C++": cplusplus,
  C: c,
  Python: python,
  Django: django,
};

const SkillCard = ({ name, description, topics }) => {
  const { isDarkMode } = useTheme();
  const getTechnologies = (topic) => {
    const cleanTopic = topic.replace(/[()]/g, "").replace(/\s+/g, " ");
    return cleanTopic.split(/,|\s+/).filter(Boolean);
  };

  return (
    <div
      className={`p-4 sm:p-6 rounded-xl border-2 transition-all duration-300 bg-white border-green-600 hover:border-black shadow-lg`}
    >
      <h3
        className="text-xl sm:text-2xl font-bold mb-4 text-black"
      >
        {name}
      </h3>
      <p
        className="mb-4 sm:mb-6 text-base sm:text-lg text-green-900"
      >
        {description}
      </p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-4 justify-items-center">
        {topics.map((topic, idx) => {
          const technologies = getTechnologies(topic);
          return (
            <div key={idx} className="w-full">
              {technologies.map((tech, tIdx) => {
                const cleanTech = tech.includes(" ")
                  ? tech
                  : tech.replace(/[^a-zA-Z+]/g, "");
                const logoKey = cleanTech;
                const logo = skillLogos[logoKey];
                if (logo) {
                  return (
                    <div key={tIdx} className="relative group flex items-center justify-center" title={tech}>
                      {typeof logo === "string" ? (
                        <img
                          src={logo}
                          alt={tech}
                          className="w-8 h-8 sm:w-12 sm:h-12 object-contain transition-transform duration-300 group-hover:scale-125"
                        />
                      ) : (
                        <span className="transition-transform duration-300 group-hover:scale-125 inline-flex items-center justify-center w-8 h-8 sm:w-12 sm:h-12">{logo}</span>
                      )}
                      <span
                        className={`absolute -bottom-8 left-1/2 transform -translate-x-1/2 px-2 sm:px-3 py-1 sm:py-2 rounded-full text-xs whitespace-nowrap bg-green-600 text-white group-hover:opacity-100 opacity-0 transition-opacity duration-300`}
                      >
                        {tech}
                      </span>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const skillCards = [
  {
    name: "Frontend Development",
    description: "Building responsive and interactive user interfaces",
    topics: ["HTML", "CSS", "JavaScript", "React", "TailwindCSS", "Bootstrap","Nextjs", "TypeScript"],
  },
  {
    name: "Backend Development",
    description: "Creating robust server-side applications",
    topics: ["Nodejs", "PostgreSQL", "Python", "Django", "FastAPI"],
  },
  {
    name: "Version Control",
    description: "Collaborative development and code management",
    topics: ["Git", "GitHub"],
  },
  {
    name: "Deployment",
    description: "Cloud and hosting solutions",
    topics: ["Netlify", "Vercel"],
  },
];

const Skills = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto px-2 sm:px-4 py-6 sm:py-12"
    >
      {skillCards.map((card, index) => (
        <SkillCard key={index} {...card} />
      ))}
    </motion.div>
  );
};

export default Skills;
